import React, { useMemo, useRef } from 'react';
import { Box, Typography, Card, Link, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { tourItinerary, places } from '../config/info';

const CARD_W = 300;
const GAP = 16;

const mapsSearchUrl = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || '')}`;

function makeMapsEmbedSrc(p) {
  if (typeof p?.mapEmbedUrl === 'string' && p.mapEmbedUrl.trim()) return p.mapEmbedUrl.trim();
  const q = encodeURIComponent(p?.mapQuery || p?.name || '');
  return `https://www.google.com/maps?q=${q}&z=14&output=embed`;
}

function ItineraryPlaceCard({ place, title, mapQueryFallback, inCarousel }) {
  const name = title || place?.name || 'Place';
  const img = place?.imageUrl;
  const mapSrc = makeMapsEmbedSrc(
    place || { mapQuery: mapQueryFallback, name }
  );
  const km =
    typeof place?.distanceKm === 'number' && Number.isFinite(place.distanceKm)
      ? place.distanceKm
      : null;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid var(--color-sand)',
        bgcolor: 'white',
        ...(inCarousel
          ? {
              flex: `0 0 ${CARD_W}px`,
              width: CARD_W,
              scrollSnapAlign: 'start',
            }
          : {
              maxWidth: 520,
              width: '100%',
              mx: 'auto',
            }),
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', bgcolor: 'var(--color-sand)' }}>
        {img ? (
          <Box
            component="img"
            src={img}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.28) 70%, rgba(0,0,0,0.55) 100%)',
          }}
        />
        {km != null && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              px: 1,
              py: 0.5,
              borderRadius: 999,
              bgcolor: 'rgba(0,0,0,0.45)',
              border: '1px solid rgba(255,255,255,0.18)',
              color: 'rgba(250,248,245,0.95)',
              fontSize: '0.8rem',
              fontWeight: 700,
              backdropFilter: 'blur(6px)',
            }}
          >
            {km} km
          </Box>
        )}
        <Typography
          component="h4"
          sx={{
            position: 'absolute',
            left: 14,
            right: 14,
            bottom: 12,
            color: 'rgba(250,248,245,0.98)',
            fontWeight: 700,
            fontSize: '1.02rem',
            lineHeight: 1.2,
            textShadow: '0 1px 10px rgba(0,0,0,0.6)',
          }}
        >
          {name}
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', width: '100%', height: 180, bgcolor: 'var(--color-cream)' }}>
        <Box
          component="iframe"
          src={mapSrc}
          title={`${name} map`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      </Box>

      <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid var(--color-sand)', textAlign: 'center' }}>
        <Link
          href={mapsSearchUrl(place?.mapQuery || mapQueryFallback || name)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'var(--color-accent)', fontSize: '0.85rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Open in Maps
        </Link>
      </Box>
    </Card>
  );
}

function DayStopsCarousel({ stops, placeById }) {
  const scrollerRef = useRef(null);
  const scrollBy = (deltaCards) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: deltaCards * (CARD_W + GAP), behavior: 'smooth' });
  };

  const list = Array.isArray(stops) ? stops : [];
  if (list.length === 0) return null;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          mb: 1.5,
        }}
      >
        <IconButton
          onClick={() => scrollBy(-1)}
          aria-label="Scroll places left"
          size="small"
          sx={{
            border: '1px solid var(--color-sand)',
            bgcolor: 'rgba(0,0,0,0.02)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          onClick={() => scrollBy(1)}
          aria-label="Scroll places right"
          size="small"
          sx={{
            border: '1px solid var(--color-sand)',
            bgcolor: 'rgba(0,0,0,0.02)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <Box
        ref={scrollerRef}
        sx={{
          display: 'flex',
          gap: `${GAP}px`,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          pb: 1,
          mx: -1,
          px: 1,
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(44,41,38,0.18)', borderRadius: 10 },
          '&::-webkit-scrollbar-track': { background: 'rgba(44,41,38,0.06)', borderRadius: 10 },
        }}
      >
        {list.map((s, i) => {
          const place = s?.placeId ? placeById.get(s.placeId) : null;
          const baseLabel = place?.name || s?.name || `Stop ${i + 1}`;
          const title = s?.label ? `${baseLabel} (${s.label})` : baseLabel;
          const mapQueryFallback = s?.mapQuery || place?.mapQuery || baseLabel;

          return (
            <ItineraryPlaceCard
              key={`${s.placeId || baseLabel}-${i}`}
              place={place}
              title={title}
              mapQueryFallback={mapQueryFallback}
              inCarousel
            />
          );
        })}
      </Box>
    </Box>
  );
}

const TourPlanSection = () => {
  const data = tourItinerary;
  const placeById = useMemo(() => {
    const m = new Map();
    (Array.isArray(places) ? places : []).forEach((p) => {
      if (p?.id) m.set(p.id, p);
    });
    return m;
  }, []);
  const days = useMemo(() => (data?.days && Array.isArray(data.days) ? data.days : []), [data]);

  if (!data || days.length === 0) return null;

  return (
    <Box
      id="tour-plan"
      sx={{
        py: { xs: 7, md: 9 },
        px: 3,
        bgcolor: 'white',
        borderTop: '1px solid rgba(44,41,38,0.06)',
        borderBottom: '1px solid rgba(44,41,38,0.06)',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: 3, color: 'var(--color-warm-gray)', fontWeight: 500, fontSize: '0.85rem' }}
        >
          Suggested tour
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 500,
            mt: 1,
            mb: 1,
            color: 'var(--color-charcoal)',
          }}
        >
          {data.title}
        </Typography>

        {days.map((d) => (
          <Box
            key={d.day}
            sx={{
              mb: { xs: 5, md: 6 },
              pb: { xs: 4, md: 5 },
              borderBottom: '1px solid rgba(44,41,38,0.08)',
              '&:last-of-type': { borderBottom: 'none', pb: 0, mb: 0 },
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: { xs: '1.65rem', md: '2rem' },
                fontWeight: 600,
                color: 'var(--color-charcoal)',
                mb: 1,
              }}
            >
              Day {d.day} — {d.title}
            </Typography>
            {d.pickupDrop ? (
              <Typography sx={{ color: 'var(--color-warm-gray)', fontSize: '0.95rem', mb: 2.5 }}>
                {d.pickupDrop}
              </Typography>
            ) : null}

            <DayStopsCarousel stops={d.stops} placeById={placeById} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TourPlanSection;
